/**
 * Popups list — same single-screen + dialog pattern as Announcements.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Plus, Trash2, MoreHorizontal } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

import { api } from "@/lib/api";
import { fetchAll } from "@/lib/fetchAll";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { StatusBadge } from "@/components/StatusBadge";
import { PageHeader } from "@/components/PageHeader";
import { PopupDialog, type PopupRow } from "@/components/popups/PopupDialog";

export const Route = createFileRoute("/_app/popups/")({ component: PopupsPage });

function PopupsPage() {
  const { can } = useAuth();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<PopupRow | null>(null);
  const [creating, setCreating] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["popups", "list"],
    queryFn: () => fetchAll<PopupRow>("/admin/popups"),
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.del(`/admin/popups/${id}`),
    onSuccess: () => { toast.success("Removed"); qc.invalidateQueries({ queryKey: ["popups"] }); },
  });

  return (
    <div className="space-y-2">
      <PageHeader
        title="Popups"
        description="Scheduled overlays that appear on chosen pages. Auto-promoted live and archived by cron."
        actions={can("popups", "create") && (
          <Button onClick={() => setCreating(true)}><Plus className="h-4 w-4" /> New popup</Button>
        )}
      />

      <Card>
        <CardContent className="px-0 pt-0 pb-0">
          {isLoading ? (
            <p className="py-12 text-center text-sm text-muted-foreground">Loading…</p>
          ) : (data?.items ?? []).length === 0 ? (
            <p className="py-12 text-center text-sm text-muted-foreground">No popups scheduled.</p>
          ) : (
            <ul className="divide-y">
              {data!.items.map((p) => (
                <li key={p.id} className="flex items-center gap-3 px-4 py-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-foreground">{p.title}</p>
                      <StatusBadge status={p.status} />
                      {p.target_pages_csv && (
                        <span className="text-xs text-muted-foreground">on {p.target_pages_csv}</span>
                      )}
                    </div>
                    {(p.starts_at || p.ends_at) && (
                      <p className="mt-1 text-xs text-muted-foreground">
                        {p.starts_at && `from ${format(p.starts_at * 1000, "MMM d, h:mma")}`}
                        {p.starts_at && p.ends_at && " · "}
                        {p.ends_at && `until ${format(p.ends_at * 1000, "MMM d, h:mma")}`}
                      </p>
                    )}
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8"><MoreHorizontal className="h-4 w-4" /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => setEditing(p)}>Edit</DropdownMenuItem>
                      <DropdownMenuItem
                        disabled={!can("popups", "delete")}
                        className="text-destructive focus:text-destructive"
                        onClick={() => { if (confirm(`Delete "${p.title}"?`)) remove.mutate(p.id); }}
                      ><Trash2 className="h-3.5 w-3.5" /> Delete</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <PopupDialog
        open={creating || !!editing}
        onOpenChange={(b) => { if (!b) { setCreating(false); setEditing(null); } }}
        popup={editing}
      />
    </div>
  );
}
